import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { ICustomRankingCreate } from '../models/iCustomRankingDto';
import { useLogger } from '../hooks/useLogger';
import ProductTypes from '../enum/ProductTypesEnum';

interface IAddCustomRankingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (ranking: ICustomRankingCreate) => Promise<boolean>;
}

const productTypeOptions = Object.values(ProductTypes);

export const AddCustomRankingDialog = ({ open, onClose, onSave }: IAddCustomRankingDialogProps) => {
  const { authState } = useOktaAuth();
  const logger = useLogger('AddCustomRankingDialog');

  const [isSaving, setIsSaving] = useState(false);

  const [salesOrg, setSalesOrg] = useState('');
  const [productYNumber, setProductYNumber] = useState('');
  const [productType, setProductType] = useState('');
  const [rank, setRank] = useState('');
  const [modifiedBy, setModifiedBy] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open && authState?.idToken?.claims.email) {
      setModifiedBy(authState.idToken.claims.email);
    }
  }, [open, authState]);

  const handleClose = () => {
    setSalesOrg('');
    setProductYNumber('');
    setProductType('');
    setRank('');
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    const tempErrors: { [key: string]: string } = {};
    if (!salesOrg) {
      tempErrors.salesOrg = 'Sales Org is required.';
    } else if (!/^\d+$/.test(salesOrg)) {
      tempErrors.salesOrg = 'Sales Org must be an integer.';
    }

    if (!productYNumber) {
      tempErrors.productYNumber = 'Product Y Number is required.';
    }

    if (!productType) {
      tempErrors.productType = 'Product Type is required.';
    }

    if (!rank) {
      tempErrors.rank = 'Rank is required.';
    } else if (!/^\d+$/.test(rank)) {
      tempErrors.rank = 'Rank must be an integer.';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const newRanking: ICustomRankingCreate = {
      salesOrg: parseInt(salesOrg, 10),
      productYNumber,
      productType,
      rank: parseInt(rank, 10),
      modifiedBy,
    };

    setIsSaving(true);
    try {
      const success = await onSave(newRanking);

      if (success) {
        handleClose();
      }
    } catch (error) {
      logger.UI.error('Save operation failed', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Custom Ranking</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid  size={12}>
              <TextField
                fullWidth
                required
                label="Sales Org"
                value={salesOrg}
                onChange={(e) => setSalesOrg(e.target.value)}
                error={!!errors.salesOrg}
                helperText={errors.salesOrg}
                disabled={isSaving}
              />
            </Grid>
            <Grid  size={12}>
              <TextField
                fullWidth
                required
                label="Product Y Number"
                value={productYNumber}
                onChange={(e) => setProductYNumber(e.target.value)}
                error={!!errors.productYNumber}
                helperText={errors.productYNumber}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                fullWidth
                options={productTypeOptions}
                value={productType}
                // Update the state when a new value is selected
                onChange={(_, newValue) => {
                  setProductType(newValue || '');
                }}
                disabled={isSaving}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Product Type"
                    error={!!errors.productType}
                    helperText={errors.productType}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                required
                label="Rank"
                type="number"
                value={rank}
                onChange={(e) => setRank(e.target.value)}
                error={!!errors.rank}
                helperText={errors.rank}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Modified By"
                value={modifiedBy}
                InputProps={{ readOnly: true }}
                variant="filled"
                disabled={isSaving}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
